import Env from "@/config/environment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthAPI } from "../api/auth";
import { ProfileAPI } from "../api/profile";
import { getToken } from "../jwt";
import type { UsersType } from "../types";

// Types
export type User = UsersType;

export interface UpdatePreferencesRequest {
  preferences: Partial<User["preferences"]>;
}

export interface UpdatePreferencesResponse {
  success: boolean;
  preferences: User["preferences"];
  message?: string;
}

// Query Keys
export const userKeys = {
  all: ["users"] as const,
  profile: () => [...userKeys.all, "profile"] as const,
  preferences: () => [...userKeys.all, "preferences"] as const,
};

// Get Current User Profile Query
// Note: This query relies on data being set from login or other sources
// since there doesn't appear to be a dedicated "get current user" endpoint
export function useUser() {
  return useQuery<User | null>({
    queryKey: userKeys.profile(),
    queryFn: async () => {
      const token = getToken();
      if (!token) return null;
      const response = await AuthAPI.renewToken({
        token,
      });
      return response.user || null;
    },
    retry: false, // Don't retry failed user fetches
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true, // Only enable when we have user data
  });
}

// Update User Preferences Hook
export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation<
    {
      success: boolean;
      user: UsersType;
    },
    Error,
    UpdatePreferencesRequest
  >({
    mutationFn: async (data) => {
      return ProfileAPI.updateProfile({
        ...data.preferences,
      });
    },
    onSuccess: () => {
      // Invalidate preferences query
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

// Update User Profile Hook (full_name and avatar)
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation<
    { success: boolean; user: User },
    Error,
    { full_name?: string; avatar?: string }
  >({
    mutationFn: async (data) => {
      return ProfileAPI.updateProfile(data);
    },
    onSuccess: (data) => {
      // Update the entire user in cache
      queryClient.setQueryData<User | null>(userKeys.profile(), data.user);

      // Invalidate to force refresh
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
}

// Helper function to set user data in cache (called from login, etc.)
export function useSetCurrentUser() {
  const queryClient = useQueryClient();

  const setUser = (user: User | null) => {
    queryClient.setQueryData(userKeys.profile(), user);
    localStorage.setItem("lastUsedUserId", user?.id || "");

    // Enable the query if we have user data
    if (user) {
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    }
  };

  const clearUser = () => {
    queryClient.setQueryData(userKeys.profile(), null);
    queryClient.removeQueries({ queryKey: userKeys.all });
  };

  return { setUser, clearUser };
}

// Language preference shortcut hook
export function useUserPreference() {
  const user = useUser();
  const updatePreferences = useUpdatePreferences();

  const updateLanguage = (lang: string) => {
    updatePreferences.mutate({
      preferences: { lang },
    });
  };

  return {
    currentLanguage: user.data?.preferences?.lang || "en",
    updateLanguage,
    isUpdating: updatePreferences.isPending,
    error: updatePreferences.error,
  };
}

export const getUserName = (user: User) => {
  if (!user) return "Unknown User";

  return (
    user.full_name?.trim() ||
    user.email
      .split("@")[0]
      .split(/[.\\-_]/)
      .map((a) => a.charAt(0).toUpperCase() + a.slice(1))
      .join(" ")
  );
};

export const getUserNameShort = (user: User | string) => {
  return (typeof user === "string" ? user : getUserName(user))
    .split(" ")
    .map((a) => (a[0] || "").trim())
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export const getFullName = (user: Pick<UsersType, "full_name" | "email">) => {
  return (
    [user.full_name]
      .filter(Boolean)
      .map(
        (name) =>
          (name || "").trim().charAt(0).toUpperCase() +
          (name || "").trim().slice(1)
      )
      .join(" ") || user.email.split("@")[0]
  );
};

export const getAvatarFullUrl = (src?: string) => {
  if (!src) return null;
  if (src.startsWith("http") || src.startsWith("data:image")) return src;
  return Env.server.replace(/\/$/, "") + src;
};
